import type { PerspectiveCamera, Vector3 } from 'three';
import type { OrbitControls } from 'three/addons/controls/OrbitControls.js';

export interface LobbyCameraState {
  readonly position: readonly [number, number, number];
  readonly target: readonly [number, number, number];
  readonly fov: number;
}

export type LobbyCameraSlot = 'lobby' | 'customizationFront' | 'customizationBack';

export interface LobbyCameraQuery {
  readonly tune: boolean;
  readonly override: LobbyCameraState | null;
}

export interface LobbyCameraTunerOverlayHandle {
  update(): void;
  dispose(): void;
}

export function readLobbyCameraQuery(search = window.location.search): LobbyCameraQuery {
  const params = new URLSearchParams(search);
  const cam = params.get('cam');
  const tune = params.has('tunecam') || cam === 'tune';
  if (cam == null || cam === 'tune') return { tune, override: null };

  try {
    const parsed = JSON.parse(cam) as unknown;
    return { tune, override: parseLobbyCameraState(parsed) };
  } catch {
    return { tune, override: null };
  }
}

export function parseLobbyCameraState(value: unknown): LobbyCameraState | null {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;
  const position = parseVectorTuple(record.position);
  const target = parseVectorTuple(record.target);
  const fov = record.fov;
  if (position == null || target == null || typeof fov !== 'number' || !Number.isFinite(fov)) {
    return null;
  }
  if (fov <= 0 || fov > 120) return null;
  return { position, target, fov };
}

export function applyLobbyCameraState(
  camera: PerspectiveCamera,
  controls: OrbitControls | null,
  state: LobbyCameraState,
): void {
  camera.position.set(state.position[0], state.position[1], state.position[2]);
  camera.fov = state.fov;
  camera.lookAt(state.target[0], state.target[1], state.target[2]);
  camera.updateProjectionMatrix();
  if (controls != null) {
    controls.target.set(state.target[0], state.target[1], state.target[2]);
    controls.update();
  }
}

export function formatLobbyCameraState(camera: PerspectiveCamera, target: Vector3): string {
  return `{ "position": ${formatTuple(camera.position.x, camera.position.y, camera.position.z)}, "target": ${formatTuple(
    target.x,
    target.y,
    target.z,
  )}, "fov": ${round(camera.fov)} }`;
}

export function createLobbyCameraTunerOverlay(opts: {
  readonly camera: PerspectiveCamera;
  readonly controls: OrbitControls;
  readonly mount?: HTMLElement;
  readonly mapName?: string;
  readonly baseCamera?: Partial<Record<LobbyCameraSlot | 'editor', LobbyCameraState>> | null;
}): LobbyCameraTunerOverlayHandle {
  const { camera, controls, mount = document.body, mapName = 'skyforge', baseCamera = null } = opts;
  let disposed = false;
  let selectedSlot: LobbyCameraSlot = 'lobby';

  const root = document.createElement('div');
  root.setAttribute('data-lobby-cam-tuner', '');
  root.style.cssText = [
    'position: fixed',
    'top: 12px',
    'right: 12px',
    'z-index: var(--z-index-tooltip, 400)',
    'width: min(320px, calc(100vw - 24px))',
    'padding: 12px',
    'border: 2px solid rgba(255, 255, 255, 0.10)',
    'border-radius: 8px',
    'background: rgba(30, 35, 50, 0.92)',
    'box-shadow: 0 4px 12px rgba(0,0,0,0.50)',
    'color: #FFFFFF',
    "font-family: 'Source Code Pro', 'Courier New', monospace",
    'font-size: 12px',
    'line-height: 1.45',
    'pointer-events: auto',
  ].join('; ');
  root.addEventListener('pointerdown', (event) => event.stopPropagation());
  root.addEventListener('wheel', (event) => event.stopPropagation());
  root.addEventListener('contextmenu', (event) => event.stopPropagation());

  const valuesEl = document.createElement('pre');
  valuesEl.style.cssText = [
    'margin: 0 0 8px',
    'white-space: pre-wrap',
    'overflow-wrap: anywhere',
    'color: #FFFFFF',
  ].join('; ');
  root.appendChild(valuesEl);

  const buttonRow = document.createElement('div');
  buttonRow.style.cssText = 'display:grid;grid-template-columns:1fr 1fr;gap:8px';
  root.appendChild(buttonRow);

  const slotSelect = document.createElement('select');
  slotSelect.setAttribute('aria-label', 'Camera slot');
  slotSelect.style.cssText = [
    'width: 100%',
    'min-height: 32px',
    'margin: 0 0 8px',
    'border: 1px solid rgba(255,255,255,0.25)',
    'border-radius: 6px',
    'background: rgba(0,0,0,0.25)',
    'color: #FFFFFF',
    "font-family: 'Source Code Pro', 'Courier New', monospace",
    'font-size: 12px',
  ].join('; ');
  for (const slot of ['lobby', 'customizationFront', 'customizationBack'] as const) {
    const option = document.createElement('option');
    option.value = slot;
    option.textContent = slot;
    slotSelect.appendChild(option);
  }
  root.insertBefore(slotSelect, buttonRow);

  const button = document.createElement('button');
  button.type = 'button';
  button.textContent = 'Copy camera';
  button.style.cssText = [
    'width: 100%',
    'min-height: 32px',
    'border: 2px solid #8A5A10',
    'border-radius: 8px',
    'background: linear-gradient(#F0B838, #C4841C)',
    'box-shadow: 0 3px 0 rgba(0,0,0,0.50)',
    'color: #FFFFFF',
    "font-family: 'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif",
    'font-size: 12px',
    'font-weight: 700',
    'cursor: pointer',
  ].join('; ');
  buttonRow.appendChild(button);

  const saveButton = button.cloneNode() as HTMLButtonElement;
  saveButton.textContent = 'Save camera.json';
  buttonRow.appendChild(saveButton);

  function currentSnippet(): string {
    return formatLobbyCameraState(camera, controls.target);
  }

  function currentCameraState(): LobbyCameraState {
    return JSON.parse(currentSnippet()) as LobbyCameraState;
  }

  function savePayloadCamera(): Record<string, LobbyCameraState> {
    const existingLobby = baseCamera?.lobby ?? currentCameraState();
    const next: Record<string, LobbyCameraState> = { lobby: existingLobby };
    if (baseCamera?.editor != null) next.editor = baseCamera.editor;
    if (baseCamera?.customizationFront != null)
      next.customizationFront = baseCamera.customizationFront;
    if (baseCamera?.customizationBack != null)
      next.customizationBack = baseCamera.customizationBack;
    next[selectedSlot] = currentCameraState();
    return next;
  }

  function update(): void {
    if (disposed) return;
    const position = camera.position;
    const target = controls.target;
    valuesEl.textContent = [
      `position: ${formatTuple(position.x, position.y, position.z)}`,
      `target:   ${formatTuple(target.x, target.y, target.z)}`,
      `fov:      ${round(camera.fov).toFixed(2)}`,
      `slot:     ${selectedSlot}`,
    ].join('\n');
  }

  slotSelect.addEventListener('change', () => {
    selectedSlot = slotSelect.value as LobbyCameraSlot;
    update();
  });

  button.addEventListener('click', () => {
    if (disposed) return;
    void copyText(currentSnippet());
    button.textContent = 'Copied';
    window.setTimeout(() => {
      if (!disposed) button.textContent = 'Copy camera';
    }, 1000);
  });

  saveButton.addEventListener('click', async () => {
    if (disposed) return;
    saveButton.disabled = true;
    try {
      const response = await fetch('/api/save-camera-json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mapName,
          camera: savePayloadCamera(),
        }),
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `HTTP ${response.status}`);
      }
      saveButton.textContent = 'Saved';
      window.setTimeout(() => {
        if (!disposed) saveButton.textContent = 'Save camera.json';
      }, 1000);
    } catch (error) {
      saveButton.textContent = 'Save failed';
      console.warn('[smashparty] camera save failed', error);
      window.setTimeout(() => {
        if (!disposed) saveButton.textContent = 'Save camera.json';
      }, 1400);
    } finally {
      saveButton.disabled = false;
    }
  });

  mount.appendChild(root);
  update();

  return {
    update,
    dispose(): void {
      if (disposed) return;
      disposed = true;
      root.remove();
    },
  };
}

function parseVectorTuple(value: unknown): [number, number, number] | null {
  if (!Array.isArray(value) || value.length !== 3) return null;
  const tuple = value as unknown[];
  if (!tuple.every((entry) => typeof entry === 'number' && Number.isFinite(entry))) return null;
  return [tuple[0] as number, tuple[1] as number, tuple[2] as number];
}

function formatTuple(x: number, y: number, z: number): string {
  return `[${round(x).toFixed(2)}, ${round(y).toFixed(2)}, ${round(z).toFixed(2)}]`;
}

function round(value: number): number {
  return Number(value.toFixed(2));
}

async function copyText(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // Fall back below; HTTP dev URLs can reject the async clipboard API.
    }
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    textarea.style.pointerEvents = 'none';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    document.execCommand('copy');
    textarea.remove();
  } catch {
    // Clipboard support is best-effort for HTTP dev URLs.
  }
}
